import { commitMutation, graphql, type Environment } from 'react-relay';

import { createAtlAttributionHeader } from '../../utils';
import type {
	emitFrontendLogsMutation,
	ForgeObservabilityPublishFrontendLogsInput,
} from './__generated__/emitFrontendLogsMutation.graphql';

export type FrontendLogSource = 'CONSOLE' | 'UNHANDLED_REJECTION' | 'UNCAUGHT_EXCEPTION';

export type FrontendLogEntry = {
	args?: string;
	errorType?: string;
	logSource: FrontendLogSource;
	lvl: number;
	msg: string;
	timestamp: number;
};

export type EmitFrontendLogsInput = {
	appId: string;
	contextAri: string;
	extensionId: string;
	installationId: string;
	logs: FrontendLogEntry[];
	relayEnvironment: Environment;
};

/**
 * Emits Forge frontend logs.
 *
 * Sends logs through `ecosystem.forgeObservability.publishFrontendLogs`.
 * Host context is trusted and attached here; `userAgent` is resolved once at the
 * network boundary so both bridge runtimes share identical behavior.
 */
export const emitFrontendLogs = (input: EmitFrontendLogsInput): Promise<void> => {
	const { relayEnvironment, appId, installationId, contextAri, extensionId, logs } = input;

	const mutationInput: ForgeObservabilityPublishFrontendLogsInput = {
		installationId,
		context: {
			appId,
			installationId,
			contextAri,
			extensionId,
			userAgent: globalThis.navigator?.userAgent ?? '',
		},
		logs: logs.map((log) => ({
			logId: crypto.randomUUID(),
			msg: log.msg,
			args: log.args,
			lvl: log.lvl,
			errorType: log.errorType,
			timestamp: log.timestamp,
			logSource: log.logSource,
		})),
	};

	return new Promise<void>((resolve) => {
		commitMutation<emitFrontendLogsMutation>(relayEnvironment, {
			mutation: graphql`
				mutation emitFrontendLogsMutation($input: ForgeObservabilityPublishFrontendLogsInput!) {
					ecosystem {
						forgeObservability {
							publishFrontendLogs(input: $input) {
								success
							}
						}
					}
				}
			`,
			variables: { input: mutationInput },
			cacheConfig: {
				metadata: {
					headers: {
						'atl-attribution': createAtlAttributionHeader(),
					},
				},
			},
			onCompleted: () => resolve(),
			onError: () => resolve(),
		});
	});
};
